'use client';
import React, { useState } from 'react';
import { Loader2, CreditCard, AlertCircle } from 'lucide-react';

interface PayFastCheckoutProps {
  amount: number;
  itemName: string;
  itemDescription?: string;
  paymentId: string;
  nameFirst?: string;
  nameLast?: string;
  email?: string;
  customStr1?: string;
  onCancel?: () => void;
  buttonLabel?: string;
  className?: string;
}

export default function PayFastCheckout({
  amount,
  itemName,
  itemDescription,
  paymentId,
  nameFirst,
  nameLast,
  email,
  customStr1,
  onCancel,
  buttonLabel = 'Pay with PayFast',
  className = '',
}: PayFastCheckoutProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handlePayment() {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/payfast/create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount.toFixed(2),
          itemName,
          itemDescription,
          paymentId,
          nameFirst,
          nameLast,
          email,
          customStr1,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to initiate payment');
      }

      const { formData, actionUrl } = data;

      // Build and submit hidden form to redirect to PayFast
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = actionUrl;

      Object.entries(formData as Record<string, string>).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err: any) {
      setError(err.message || 'Payment initiation failed. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {error && (
        <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-xl">
          <AlertCircle size={15} className="text-danger shrink-0 mt-0.5" />
          <p className="text-xs text-danger">{error}</p>
        </div>
      )}
      <div className="flex gap-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handlePayment}
          disabled={loading}
          className={`flex items-center justify-center gap-2 py-3 px-5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed ${onCancel ? 'flex-1' : 'w-full'} ${className}`}
        >
          {loading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Redirecting to PayFast...
            </>
          ) : (
            <>
              <CreditCard size={15} />
              {buttonLabel}
            </>
          )}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Secured by{' '}
        <span className="font-semibold text-foreground">PayFast</span> · ZAR · PCI DSS Level 1
      </p>
    </div>
  );
}
